import { format, fromUnixTime, subDays } from 'date-fns';
import { id } from 'date-fns/locale';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

/**
 * Add a specified number of seconds or minutes to the current time.
 *
 * Example:
 *  - const date = new Date('2022-12-06T12:00:00-05:00');
 *  - const updatedDate1 = addTime(date, 10); // adds 10 seconds (default units)
 *  - const updatedDate2 = addTime(date, 5, 'minutes'); // adds 5 minutes
 *
 * @param date
 * @param time
 * @param units
 * @returns number
 */
export function addTime(date: Date, time: number, units = 'seconds') {
  if (units === 'seconds') {
    date.setSeconds(date.getSeconds() + time);
  } else if (units === 'minutes') {
    date.setMinutes(date.getMinutes() + time);
  }
  return date;
}

/**
 * Convert issuet at (iat) timestamp timestamp to a date object.
 *
 * @param iat
 * @returns date
 */
export function convertJwtIatToIsoDate(iat: number, addSeconds = 0): Date {
  const isoDate = new Date(iat * 1000);
  if (addSeconds > 0) {
    isoDate.setSeconds(isoDate.getSeconds() + addSeconds);
    return isoDate;
  }
  return new Date(iat * 1000);
}

export function adminDateFormatter(value: number | undefined | null) {
  if (value) {
    const date = new Date(value);
    return format(date, 'dd/MM/yyyy h:mm a').toUpperCase();
  } else {
    return '';
  }
}

export function dateSwapper(date?: string) {
  if (date) {
    const [dd, mm, yyyy] = date.split('/');
    return [mm, dd, yyyy].join('/');
  } else {
    return '1/1/2001';
  }
}

export function unixToHumanDate(timestamp: number, formatStr?: string): string {
  const dateFormat = formatStr ? formatStr : 'dd MMMM yyyy';
  const date = fromUnixTime(timestamp);
  return format(date, dateFormat, { locale: id });
}

export function getLastDaysDate(n: number, utc = false): Date {
  const date = subDays(new Date(), n);
  return utc ? zonedTimeToUtc(date, 'Asia/Jakarta') : new Date(date);
}

export function unixTimeFromDaysAgo(n: number): number {
  const now = new Date();
  return Number((now.setDate(new Date().getDate() - n) / 1000).toFixed(0));
}

export function promoValidFromToFormat(
  from: number,
  until: number,
  showTime = true
) {
  if (from && until) {
    return `${formatInTimeZone(new Date(from), 'Asia/Jakarta', 'dd MMM yyyy', {
      locale: id,
    })} - ${formatInTimeZone(
      new Date(until),
      'Asia/Jakarta',
      `dd MMM yyyy ${showTime ? '• HH:mm zzz' : ''}`,
      {
        locale: id,
      }
    )}`.trim();
  } else {
    return '';
  }
}

export function DateFormatterIdn(
  date: Date | number | string | undefined | null
) {
  if (!date) {
    return '-';
  }
  const dateObj = new Date(date);
  return format(dateObj, 'eeee, d MMMM yyyy • HH:mm', {
    locale: id,
  });
}

/**
 * Format date string to short format with Indonesian locale.
 *
 * @example formatShortDate("2025-01-02") => "2 Jan 25"
 * @example formatShortDate(null) => "-"
 *
 * @param dateString - Date string in YYYY-MM-DD format or null
 * @returns Formatted date string (e.g., "2 Jan 25") or "-" if null
 */
export function formatShortDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return format(date, 'd MMM yy', { locale: id });
}

/**
 * Format date string to slash format (d/MM/yy) for LHC and similar displays.
 *
 * @example formatLhcDate("2025-12-02") => "2/12/25"
 * @example formatLhcDate(null) => "-"
 *
 * @param dateString - Date string in YYYY-MM-DD format or null
 * @returns Formatted date string (e.g., "2/12/25") or "-" if null
 */
export function formatLhcDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return format(date, 'd/MM/yy');
}
